import React, { useState } from 'react';
import { Sparkles, ArrowRight, FileText, Lightbulb, Zap } from 'lucide-react';
import { useSocialMediaStore } from '../../stores/socialMediaStore';
import { useUserStore } from '../../stores/userStore';
import QuickIdeasGenerator from './content/QuickIdeasGenerator';
import TailoredPlanCreator from './content/TailoredPlanCreator';
import TailoredPlanModal from './content/TailoredPlanModal';
import SocialMediaConnectModal from './content/SocialMediaConnectModal';
import SocialMediaReminder from './content/SocialMediaReminder';
import NicheGenerator from './content/NicheGenerator';
import type { Platform } from '../../stores/socialMediaStore';

interface ContentIdeasProps {
  user: {
    name: string;
    isNewUser: boolean;
  };
}

export default function ContentIdeas({ user }: ContentIdeasProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showIdeasGenerator, setShowIdeasGenerator] = useState(false);
  const [showNicheModal, setShowNicheModal] = useState(false);
  const [showSocialModal, setShowSocialModal] = useState(false);
  const accounts = useSocialMediaStore((state) => state.accounts);
  const connectedAccounts = accounts.filter(account => account.connected);

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Lightbulb className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ideias de Conteúdo</h1>
            <p className="text-gray-600">
              {connectedAccounts.length > 0 
                ? `Ideias personalizadas para ${connectedAccounts.map(a => a.platform).join(', ')}`
                : 'Conecte suas redes sociais para ideias personalizadas'}
            </p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-8">
            {/* Quick Ideas Generator */}
            <div className="bg-white/10 p-6 rounded-lg border border-gray-100 shadow-sm">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Ideias Personalizadas</h3>
                  <p className="text-sm text-gray-600">Gere ideias de conteúdo com IA</p>
                </div>
              </div>

              {!showIdeasGenerator ? (
                <button
                  onClick={() => {
                    if (connectedAccounts.length === 0) {
                      setShowSocialModal(true);
                    } else {
                      setShowIdeasGenerator(true);
                    }
                  }}
                  className="w-full py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                >
                  Gerar Novas Ideias
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              ) : (
                <QuickIdeasGenerator connectedAccounts={connectedAccounts} />
              )}
            </div>
            
            {/* Niche Generator */}
            <div className="bg-white/10 p-6 rounded-lg border border-gray-100 shadow-sm">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Zap className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Descubra seu Nicho</h3>
                  <p className="text-sm text-gray-600">Gere ideias de nicho para seu canal</p>
                </div>
              </div>

              <button
                onClick={() => {
                  if (connectedAccounts.length === 0) {
                    setShowSocialModal(true);
                  } else {
                    setShowNicheModal(true);
                  }
                }}
                className="w-full py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
              >
                Começar
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Tailored Plan Creator */}
            <div className="bg-white/10 p-6 rounded-lg border border-gray-100 shadow-sm">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Plano Personalizado</h3>
                  <p className="text-sm text-gray-600">Crie planos personalizados ilimitados</p>
                </div>
              </div>

              <button
                onClick={() => {
                  if (connectedAccounts.length === 0) {
                    setShowSocialModal(true);
                  } else {
                    setIsModalOpen(true);
                  }
                }}
                className="w-full py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
              >
                Criar Meu Plano Personalizado
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Modals */}
        {isModalOpen && (
          <TailoredPlanModal onClose={() => setIsModalOpen(false)} />
        )}

        {showSocialModal && (
          <SocialMediaConnectModal onClose={() => setShowSocialModal(false)} />
        )}

        {showNicheModal && (
          <NicheGenerator onClose={() => setShowNicheModal(false)} />
        )}
      </div>
    </div>
  );
}